import apiClient from '../api/apiClient';
import {
  AssignmentContact,
  Incident,
  IncidentListResponse,
  CreateIncidentRequest,
  CreateIncidentResponse,
  CreateIncidentResponseData,
  IncidentSummary,
  IncidentSummaryResponse,
  ActivityEntry,
  IncidentActivityResponse,
  IncidentActivityItem,
  IncidentActivityPage,
  IncidentRosterPerson,
  IncidentRosterResponse,
  StatusBatchRequest,
  StatusBatchResponse,
  StatusBatchResponseData,
  StatusUpdateInput,
  AddUnlistedPersonRequest,
  AddUnlistedPersonResponse,
  UnaccountedResponse,
  CreateCheckpointRequest,
  CreateCheckpointResponse,
  CloseIncidentRequest,
  CloseIncidentResponse,
  UpdateIncidentDetailsRequest,
  UpdateIncidentDetailsResponse,
} from '../Types/Incident';
import type { IncidentAssignmentsResponse } from '../Types/IncidentAssignment';
import { normalizeApiPersonStatus, toApiPersonStatus } from '../Utils/headcountStatus';

import { IncidentType } from '../Types/enums';

/** Map frontend string IncidentType to the numeric code the POST API expects. */
const INCIDENT_TYPE_API_CODE: Record<string, number> = {
  [IncidentType.Drill]: 0,
  [IncidentType.Fire]: 1,
  [IncidentType.Weather]: 2,
  [IncidentType.Medical]: 3,
  [IncidentType.Security]: 4,
  [IncidentType.Hazmat]: 5,
};

/** Normalize numeric incident status (0=active, 1=closed) to string. */
const normalizeIncidentStatus = (raw: unknown): string => {
  if (typeof raw === 'number') return raw === 0 ? 'active' : 'closed';
  if (typeof raw === 'string') return raw;
  return 'active';
};

export const getIncidents = async (): Promise<IncidentListResponse> => {
  const response = await apiClient.get<IncidentListResponse>('/api/incidents');
  const body = response.data;
  // Normalize assignment arrays on each incident
  if (Array.isArray(body.data)) {
    body.data = body.data.map((inc) => ({
      ...inc,
      status: normalizeIncidentStatus(inc.status),
      incidentCommanders: inc.incidentCommanders ?? [],
      headcounters: inc.headcounters ?? [],
      scopeCounts: (inc as unknown as Record<string, unknown>).scopeCounts as typeof inc.scopeCounts ?? undefined,
    }));
  }
  return body;
};

export const getIncidentDetail = async (incidentId: string): Promise<Incident> => {
  // Try dedicated detail endpoint first, fall back to finding in list
  try {
    const response = await apiClient.get<{ data: Incident } | Incident>(`/api/incidents/${encodeURIComponent(incidentId)}`);
    const body = response.data as Record<string, unknown>;
    const incident = (body.data as Incident) ?? (body as unknown as Incident);
    return { ...incident, status: normalizeIncidentStatus(incident.status) };
  } catch {
    const list = await getIncidents();
    const found = list.data.find((i) => i.incidentId === incidentId);
    if (!found) throw new Error('Incident not found');
    return { ...found, status: normalizeIncidentStatus(found.status) };
  }
};

export const createIncident = async (request: CreateIncidentRequest): Promise<CreateIncidentResponseData> => {
  const payload = {
    ...request,
    incidentType: INCIDENT_TYPE_API_CODE[request.incidentType] ?? 0,
  };
  const response = await apiClient.post<CreateIncidentResponse>('/api/incidents', payload);
  const body = response.data;
  const data = body?.data;
  const incidentId = data?.incidentId ?? body?.incidentId;
  if (!incidentId) {
    throw new Error('No incidentId returned from the API.');
  }
  // Return full response data if available, otherwise construct minimal shape
  if (data && data.incidentId) {
    return {
      ...data,
      status: normalizeIncidentStatus(data.status),
    };
  }
  return {
    incidentId,
    incidentName: request.incidentName,
    incidentType: request.incidentType,
    location: request.location ?? null,
    status: 'active',
    startedAtUtc: new Date().toISOString(),
  };
};

const normalizeSummary = (raw: IncidentSummaryResponse): { summary: IncidentSummary; serverTimeUtc?: string } => {
  const inner = (raw.data ?? raw) as Record<string, unknown>;
  // API may nest counts under a `summary` sub-object
  const counts = (inner.summary as Record<string, unknown>) ?? inner;
  return {
    summary: {
      ...(inner as unknown as IncidentSummary),
      // Incident metadata — may live at inner or nested summary level
      // Use undefined (not 0) as fallback so consuming pages can fall through to incident detail
      incidentName: (inner.incidentName as string) ?? (counts.incidentName as string) ?? '',
      incidentType: (inner.incidentType as string) ?? (counts.incidentType as string) ?? undefined,
      location: (inner.location as string) ?? (inner.locationName as string) ?? (counts.location as string) ?? (counts.locationName as string) ?? '',
      startedAtUtc: (inner.startedAtUtc as string) ?? (counts.startedAtUtc as string) ?? '',
      closedAtUtc: (inner.closedAtUtc as string | null) ?? (counts.closedAtUtc as string | null) ?? null,
      status: normalizeIncidentStatus(inner.status ?? counts.status),
      // Counts
      total: (counts.total as number) ?? 0,
      accounted: (counts.accounted as number) ?? 0,
      notAccounted: (counts.notAccounted as number) ?? 0,
      notChecked: (counts.notChecked as number) ?? 0,
      tenants: (counts.tenants as number) ?? 0,
      percentComplete: (counts.percentComplete as number) ?? 0,
      unsyncedChanges: (inner.unsyncedChanges as number) ?? 0,
      accountabilityByView: (inner.accountabilityByView as IncidentSummary['accountabilityByView']) ?? [],
      headcounterActivity: (inner.headcounterActivity as IncidentSummary['headcounterActivity']) ?? [],
      incidentCommanders: (inner.incidentCommanders as AssignmentContact[]) ?? [],
      headcounters: (inner.headcounters as AssignmentContact[]) ?? [],
      assignedOffice: (counts.assignedOffice as number) ?? undefined,
      assignedOfficeNotBadgedIn: (counts.assignedOfficeNotBadgedIn as number) ?? undefined,
      scopeCounts: (counts.scopeCounts as IncidentSummary['scopeCounts']) ?? (inner.scopeCounts as IncidentSummary['scopeCounts']) ?? undefined,
      visitorCount: (counts.visitorCount as number) ?? (inner.visitorCount as number) ?? undefined,
    },
    serverTimeUtc: raw.meta?.serverTimeUtc,
  };
};

export const getIncidentSummary = async (incidentId: string): Promise<{ summary: IncidentSummary; serverTimeUtc?: string }> => {
  const response = await apiClient.get<IncidentSummaryResponse>(`/api/incidents/${encodeURIComponent(incidentId)}/summary`);
  return normalizeSummary(response.data);
};

export const getIncidentActivity = async (incidentId: string): Promise<{ activity: ActivityEntry[]; serverTimeUtc?: string }> => {
  const response = await apiClient.get<IncidentActivityResponse>(`/api/incidents/${encodeURIComponent(incidentId)}/activity?pageNumber=1&pageSize=50`);
  const body = response.data;
  const dataPayload = body.data as unknown as Record<string, unknown>;
  // API returns paginated { data: { items: [...] } } or flat { data: [...] }
  const rawItems: Record<string, unknown>[] = Array.isArray(dataPayload)
    ? dataPayload
    : Array.isArray(dataPayload?.items)
      ? dataPayload.items as Record<string, unknown>[]
      : [];
  // Map API fields to the ActivityEntry shape used by components
  const resolveName = (item: Record<string, unknown>): string => {
    const raw = (item.personDisplayName as string) ?? (item.actorDisplayName as string) ?? (item.personName as string) ?? '';
    // API sometimes returns generic placeholder names — fall back to updatedBy or a clean label
    if (!raw || raw === 'Unknown User' || raw === 'System') {
      return (item.actorDisplayName as string) && (item.actorDisplayName as string) !== 'Unknown User'
        ? (item.actorDisplayName as string)
        : (item.updatedBy as string) ?? 'Incident Commander';
    }
    return raw;
  };
  const activity: ActivityEntry[] = rawItems.map((item) => ({
    timestamp: (item.occurredAtUtc as string) ?? (item.timestamp as string) ?? '',
    personName: resolveName(item),
    action: (item.activityType as string) ?? (item.action as string) ?? '',
    context: (item.details as string) ?? (item.context as string) ?? '',
    updatedBy: (item.actorDisplayName as string) ?? (item.updatedBy as string),
  }));
  return {
    activity,
    serverTimeUtc: body.meta?.serverTimeUtc,
  };
};

// ── Paginated Activity Log ──

export const getIncidentActivityPage = async (
  incidentId: string,
  pageNumber: number,
  pageSize: number,
): Promise<IncidentActivityPage> => {
  const response = await apiClient.get(
    `/api/incidents/${encodeURIComponent(incidentId)}/activity?pageNumber=${pageNumber}&pageSize=${pageSize}`,
  );
  const body = response.data;

  // Normalize: API may return { data: { items, ... } } or { data: [...] } or { items: [...] }
  const dataPayload = body.data ?? body;
  let items: IncidentActivityItem[];
  let totalCount: number | undefined;
  let totalPages: number | undefined;

  if (Array.isArray(dataPayload)) {
    items = dataPayload;
  } else if (Array.isArray(dataPayload?.items)) {
    items = dataPayload.items;
    totalCount = typeof dataPayload.totalCount === 'number' ? dataPayload.totalCount : undefined;
    totalPages = typeof dataPayload.totalPages === 'number' ? dataPayload.totalPages : undefined;
  } else {
    items = [];
  }

  return {
    items,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: totalPages != null ? pageNumber < totalPages : items.length === pageSize,
  };
};

// ── Headcount / Roster ──

export const getIncidentRoster = async (
  incidentId: string,
  params?: { viewId?: string; status?: string; search?: string; pageSize?: number; scope?: string },
): Promise<{ people: IncidentRosterPerson[]; serverTimeUtc?: string }> => {
  const effectivePageSize = params?.pageSize ?? 500;
  let allPeople: IncidentRosterPerson[] = [];
  let serverTimeUtc: string | undefined;
  let page = 1;

  // Fetch pages until we receive fewer items than the page size
  while (true) {
    const query = new URLSearchParams();
    if (params?.viewId) query.set('viewId', params.viewId);
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    if (params?.scope) query.set('scope', params.scope);
    query.set('pageSize', String(effectivePageSize));
    query.set('page', String(page));
    const qs = query.toString();
    const url = `/api/incidents/${encodeURIComponent(incidentId)}/roster${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<IncidentRosterResponse>(url);
    const body = response.data;

    // Flexibly extract people array from multiple possible shapes
    let people: IncidentRosterPerson[] = [];
    if (Array.isArray(body.data)) {
      people = body.data;
    } else if (body.data && typeof body.data === 'object') {
      const dataObj = body.data as Record<string, unknown>;
      if (Array.isArray(dataObj.people)) {
        people = dataObj.people as IncidentRosterPerson[];
      } else if (Array.isArray(dataObj.items)) {
        people = dataObj.items as IncidentRosterPerson[];
      }
    } else if (Array.isArray(body.people)) {
      people = body.people;
    }

    // Add a console warning in dev if we got a response but couldn't extract people
    if (people.length === 0 && body.data && page === 1) {
      console.warn('[getIncidentRoster] Could not extract people from response shape:', JSON.stringify(body).substring(0, 500));
    }

    // Normalize status: API sends camelCase strings ("accounted", "notAccounted", "notChecked").
    // Also handles legacy numeric values during migration.
    // Map new API badge/source fields onto the person model.
    people = people.map((p) => {
      const raw = p as unknown as Record<string, unknown>;
      return {
        ...p,
        status: normalizeApiPersonStatus(p.status),
        rosterSource: (raw.rosterSource as string) ?? p.rosterSource ?? undefined,
        lastIncidentDayBadgeActivity: (raw.lastIncidentDayBadgeActivity as IncidentRosterPerson['lastIncidentDayBadgeActivity']) ?? p.lastIncidentDayBadgeActivity ?? undefined,
        lobbyTrackActivity: (raw.lobbyTrackActivity as IncidentRosterPerson['lobbyTrackActivity']) ?? p.lobbyTrackActivity ?? undefined,
        isAssignedToIncidentOffice: typeof raw.isAssignedToIncidentOffice === 'boolean' ? raw.isAssignedToIncidentOffice : (p.isAssignedToIncidentOffice ?? false),
        hasIncidentDayBadgeActivity: typeof raw.hasIncidentDayBadgeActivity === 'boolean' ? raw.hasIncidentDayBadgeActivity : (p.hasIncidentDayBadgeActivity ?? false),
        assignedOfficeLocationId: (raw.assignedOfficeLocationId as string) ?? p.assignedOfficeLocationId ?? undefined,
        assignedOfficeLocationName: (raw.assignedOfficeLocationName as string) ?? p.assignedOfficeLocationName ?? undefined,
        officeCubeNumber: (raw.officeCubeNumber as string) ?? p.officeCubeNumber ?? undefined,
        inclusionReasons: (raw.inclusionReasons as IncidentRosterPerson['inclusionReasons']) ?? p.inclusionReasons ?? [],
        isInAccountabilityScope: typeof raw.isInAccountabilityScope === 'boolean' ? raw.isInAccountabilityScope : undefined,
        isDefaultVisible: typeof raw.isDefaultVisible === 'boolean' ? raw.isDefaultVisible : undefined,
        isAssignedOfficeNoBadgeToday: typeof raw.isAssignedOfficeNoBadgeToday === 'boolean' ? raw.isAssignedOfficeNoBadgeToday : undefined,
      };
    });

    allPeople = allPeople.concat(people);

    // Capture serverTimeUtc from first page
    if (body.meta?.serverTimeUtc) {
      serverTimeUtc = body.meta.serverTimeUtc;
    }

    // If we got fewer items than the page size, we've fetched everything
    if (people.length < effectivePageSize) break;

    page++;
  }

  return {
    people: allPeople,
    serverTimeUtc,
  };
};

export const submitStatusBatch = async (
  incidentId: string,
  updates: StatusUpdateInput[],
): Promise<{ responseData: StatusBatchResponseData | null; serverTimeUtc?: string }> => {
  const request: StatusBatchRequest = {
    clientBatchId: crypto.randomUUID(),
    clientSubmittedAtUtc: new Date().toISOString(),
    items: updates.map((u) => ({
      clientUpdateId: crypto.randomUUID(),
      personId: u.personId,
      status: toApiPersonStatus(u.status),
      observedAtUtc: u.observedAtUtc,
      ...(u.notes ? { notes: u.notes } : {}),
    })),
  };
  const response = await apiClient.post<StatusBatchResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/status-batches`,
    request,
  );
  return {
    responseData: response.data?.data ?? null,
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const addUnlistedPerson = async (
  incidentId: string,
  request: AddUnlistedPersonRequest,
): Promise<{ person?: IncidentRosterPerson; serverTimeUtc?: string }> => {
  const response = await apiClient.post<AddUnlistedPersonResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/unlisted-persons`,
    request,
  );
  const body = response.data;
  const raw = body?.data as unknown as Record<string, unknown> | undefined;
  const person: IncidentRosterPerson | undefined = raw
    ? { ...(raw as unknown as IncidentRosterPerson), status: normalizeApiPersonStatus(raw.status) }
    : undefined;
  return {
    person,
    serverTimeUtc: body?.meta?.serverTimeUtc,
  };
};

export const getUnaccountedPeople = async (
  incidentId: string,
  params?: { status?: string; search?: string },
): Promise<{ notAccounted: IncidentRosterPerson[]; serverTimeUtc?: string }> => {
  const normalizePerson = (raw: Record<string, unknown>): IncidentRosterPerson => ({
    personId: raw.personId as string,
    displayName: raw.displayName as string,
    email: (raw.email as string) ?? undefined,
    phone: (raw.phone as string) ?? undefined,
    personType: (raw.personType != null ? String(raw.personType) : undefined),
    practice: (raw.practice as string) ?? undefined,
    floor: (raw.floor as string) ?? undefined,
    location: (raw.location as string) ?? undefined,
    companyName: (raw.companyName as string) ?? undefined,
    tenantName: (raw.tenantName as string) ?? undefined,
    rosterSource: (raw.rosterSource as string) ?? undefined,
    lastIncidentDayBadgeActivity: (raw.lastIncidentDayBadgeActivity as IncidentRosterPerson['lastIncidentDayBadgeActivity']) ?? undefined,
    lobbyTrackActivity: (raw.lobbyTrackActivity as IncidentRosterPerson['lobbyTrackActivity']) ?? undefined,
    isAssignedToIncidentOffice: typeof raw.isAssignedToIncidentOffice === 'boolean' ? raw.isAssignedToIncidentOffice : false,
    hasIncidentDayBadgeActivity: typeof raw.hasIncidentDayBadgeActivity === 'boolean' ? raw.hasIncidentDayBadgeActivity : false,
    assignedOfficeLocationId: (raw.assignedOfficeLocationId as string) ?? undefined,
    assignedOfficeLocationName: (raw.assignedOfficeLocationName as string) ?? undefined,
    officeCubeNumber: (raw.officeCubeNumber as string) ?? undefined,
    inclusionReasons: (raw.inclusionReasons as IncidentRosterPerson['inclusionReasons']) ?? [],
    lastBadgeAtUtc: (raw.lastBadgeAtUtc as string) ?? undefined,
    lastBadgeLocation: (raw.lastBadgeLocation as string) ?? undefined,
    lastBadgeReaderName: (raw.lastBadgeReaderName as string) ?? undefined,
    lastBadgeDoorName: (raw.lastBadgeDoorName as string) ?? undefined,
    lobbyTrackCheckInAtUtc: (raw.lobbyTrackCheckInAtUtc as string) ?? undefined,
    hasBadgeActivityToday: typeof raw.hasBadgeActivityToday === 'boolean' ? raw.hasBadgeActivityToday : undefined,
    status: normalizeApiPersonStatus(raw.status),
    lastUpdatedAtUtc: (raw.lastUpdatedAtUtc as string) ?? undefined,
    lastUpdatedByName: (raw.lastUpdatedByDisplayName as string) ?? (raw.lastUpdatedByName as string) ?? undefined,
    isUnlisted: (raw.isUnlisted as boolean) ?? undefined,
  });

  const extractItems = (body: UnaccountedResponse): Record<string, unknown>[] => {
    const dataObj = body.data && !Array.isArray(body.data) ? body.data : null;
    const src = dataObj ?? body;
    if (Array.isArray(src)) return src as Record<string, unknown>[];
    if (src && typeof src === 'object') {
      const s = src as Record<string, unknown>;
      if (Array.isArray(s.items)) return s.items as Record<string, unknown>[];
      if (Array.isArray(s.people)) return s.people as Record<string, unknown>[];
    }
    return [];
  };

  const baseUrl = `/api/incidents/${encodeURIComponent(incidentId)}/unaccounted`;
  const searchParam = params?.search ? `&search=${encodeURIComponent(params.search)}` : '';
  const statusParam = params?.status ? `status=${encodeURIComponent(params.status)}` : 'status=notAccounted';

  const res = await apiClient.get<UnaccountedResponse>(`${baseUrl}?${statusParam}${searchParam}`);

  const notAccounted = extractItems(res.data).map((p) => normalizePerson(p));

  return {
    notAccounted,
    serverTimeUtc: res.data?.meta?.serverTimeUtc,
  };
};

export const createCheckpoint = async (
  incidentId: string,
  request: CreateCheckpointRequest,
): Promise<{ serverTimeUtc?: string }> => {
  const response = await apiClient.post<CreateCheckpointResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/checkpoints`,
    request,
  );
  return {
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const closeIncident = async (
  incidentId: string,
  request: CloseIncidentRequest,
): Promise<{ serverTimeUtc?: string }> => {
  const response = await apiClient.post<CloseIncidentResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/close`,
    request,
  );
  return {
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};

export const getIncidentAssignments = async (
  incidentId: string,
): Promise<IncidentAssignmentsResponse> => {
  const response = await apiClient.get<IncidentAssignmentsResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/assignments`,
  );
  const body = response.data;
  // Normalize badge/roster fields on each assignment
  if (body.data?.assignments) {
    body.data.assignments = body.data.assignments.map((a) => {
      const raw = a as unknown as Record<string, unknown>;
      return {
        ...a,
        status: raw.status != null ? normalizeApiPersonStatus(raw.status) : undefined,
        rosterSource: (raw.rosterSource as string) ?? undefined,
        lastIncidentDayBadgeActivity: (raw.lastIncidentDayBadgeActivity as typeof a.lastIncidentDayBadgeActivity) ?? undefined,
        lobbyTrackActivity: (raw.lobbyTrackActivity as typeof a.lobbyTrackActivity) ?? undefined,
        isAssignedToIncidentOffice: typeof raw.isAssignedToIncidentOffice === 'boolean' ? raw.isAssignedToIncidentOffice : undefined,
        hasIncidentDayBadgeActivity: typeof raw.hasIncidentDayBadgeActivity === 'boolean' ? raw.hasIncidentDayBadgeActivity : undefined,
        inclusionReasons: (raw.inclusionReasons as typeof a.inclusionReasons) ?? undefined,
        isUnlisted: typeof raw.isUnlisted === 'boolean' ? raw.isUnlisted : undefined,
        isAssignedOfficeNoBadgeToday: typeof raw.isAssignedOfficeNoBadgeToday === 'boolean' ? raw.isAssignedOfficeNoBadgeToday : undefined,
      };
    });
  }
  return body;
};

// ── Edit Incident Details ──

export const updateIncidentDetails = async (
  incidentId: string,
  request: UpdateIncidentDetailsRequest,
): Promise<{ incident?: Incident; serverTimeUtc?: string }> => {
  const payload = {
    ...request,
    ...(request.incidentType != null
      ? { incidentType: INCIDENT_TYPE_API_CODE[request.incidentType] ?? request.incidentType }
      : {}),
  };
  const response = await apiClient.patch<UpdateIncidentDetailsResponse>(
    `/api/incidents/${encodeURIComponent(incidentId)}/details`,
    payload,
  );
  return {
    incident: response.data?.data ?? undefined,
    serverTimeUtc: response.data?.meta?.serverTimeUtc,
  };
};
